import { logs } from "@opentelemetry/api-logs";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query";
import { fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";

import { bffBaseUrl } from "@/lib/bffBase";
import {
  bffOriginPattern,
  joinBffUrl,
  templatizeBffPath,
} from "@/lib/bffTransport";
import {
  noteDirectBffReached,
  noteDirectBffUnreachable,
} from "@/lib/directBffReachability";
import { isTestEnv } from "@/lib/env";
import { isMockModeRequest, requestMockReactivation } from "@/lib/mockMode";
import { noteSilentSignal } from "@/lib/noteSilentSignal";
import { getOidcUserManager } from "@/providers/AuthProvider/userManagerSingleton";
import { paramsSerializer } from "@/utils";

import { captureRtkQueryError } from "./sentryErrorCapture";
import { shouldRetryRequest } from "./shouldRetryRequest";
import {
  normalizeFetchArgs,
  withBootFetchTimeout,
} from "./withBootFetchTimeout";

// Per-fetch logger. Backed by the global LoggerProvider set up in
// `apps/web/lib/otel/logger.ts` — emits go through the BatchLogRecordProcessor
// → OTLP exporter → the BFF's /diag/logs (a cross-origin keep-alive fetch) →
// Kibana, with the active span's trace_id auto-attached for trace↔log
// correlation.
const apiLogger = logs.getLogger("customer-web-rtk-query");

// No fetch-level `cache` override: the BFF's CacheControlInterceptor declares
// cacheability per route — refdata/boot GETs carry `private, max-age,
// stale-while-revalidate` (instant warm repeat loads), and every other GET
// carries an explicit `private, no-store`, so nothing is ever heuristically
// cached. A blanket `cache: "no-store"` here would make those SWR policies
// inert again.
//
// No `baseUrl` either: `rawBaseQuery` reads the BFF base per call
// (lib/bffBase.ts) and passes an already-joined URL.
const fetchQuery = fetchBaseQuery({
  paramsSerializer,
  prepareHeaders: async (headers, { extraOptions }) => {
    // The page's latched decision (MockModeGate), never the shared storage
    // flag, which another tab can flip under a live page.
    const isMock = isMockModeRequest();
    // @ts-expect-error No way to override extraOptions type
    if (!extraOptions?.external) {
      // Mock mode sends NO Authorization header: the MSW handlers never check
      // it, and a stored real token must not ride along on any request.
      if (typeof window !== "undefined" && !isMock) {
        const user = localStorage.getItem(
          `oidc.user:${process.env.NEXT_PUBLIC_AUTH_URL}:${process.env.NEXT_PUBLIC_CLIENT_ID}`
        );
        if (user) {
          const token = JSON.parse(user).access_token;
          headers.set("authorization", `Bearer ${token}`);
        }
      }

      if (!headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }
    }

    return headers;
  },
});

type FetchQueryResult = Awaited<ReturnType<typeof fetchQuery>>;
type FetchQueryApi = Parameters<typeof fetchQuery>[1];

const IDEMPOTENT_METHODS: ReadonlySet<string> = new Set(["GET", "HEAD"]);

/**
 * A direct mutation that got no response at all. With no gateway in the path
 * there is no status to tell "rejected before the BFF" from "sent and
 * maybe applied", so this is the trail for it: one console line (Kibana via
 * the OTel console bridge, the replay console), no Sentry issue. It is never
 * retried — `shouldRetryRequest` refuses every non-idempotent method — and
 * the money flows treat `FETCH_ERROR` as an unknown outcome and re-read
 * (`utils/mutationOutcome.ts`). Tags carry the method and the templatized
 * path only, never ids, query strings or the body.
 */
function noteDirectMutationNetworkError(
  api: FetchQueryApi,
  method: string,
  url: string
): void {
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  const connection = (
    nav as (Navigator & { connection?: { effectiveType?: string } }) | undefined
  )?.connection;
  noteSilentSignal({
    message: "[Infra] Direct BFF mutation got no response",
    signal: "direct-bff-mutation-network-error",
    tags: {
      endpoint: api.endpoint,
      method,
      path: templatizeBffPath(url),
      online: String(nav?.onLine ?? "unknown"),
      effective_type: connection?.effectiveType ?? "unknown",
    },
  });
}

/**
 * `fetchQuery` on the BFF origin (lib/bffBase.ts, lib/bffTransport.ts). There
 * is no other transport and no fallback — a failed call is returned as it
 * failed.
 *
 * Plus one mock-mode hook: the MSW worker answers every mock-mode call on the
 * BFF origin, so one that gets no response at all (`FETCH_ERROR`, not
 * aborted) went past the worker to the network — the worker has lost this
 * page (restart/update). Ask MockModeGate to re-assert mocking right away,
 * per attempt, so the retry layer's later attempts can already land on the
 * worker. The result is returned unchanged; retry semantics are untouched.
 * It fires only where the lost call fails status-less, that is on a page
 * origin the BFF's CORS list refuses. On an admitted origin (`localhost:3000`,
 * a deployed UAT host with `?mockMode=true`) the BFF answers the
 * bearer-less call with a 401, and recovery waits for the gate's
 * `visibilitychange`/`controllerchange` re-assertion
 * (.claude/docs/web-mock-mode.md, "Worker restart / update mid-session").
 *
 * External calls (`extraOptions.external`) carry absolute URLs and pass
 * through untouched.
 */
const rawBaseQuery: typeof fetchQuery = async (args, api, extraOptions) => {
  const fetchArgs = normalizeFetchArgs(args);
  const method = (fetchArgs.method ?? "GET").toUpperCase();
  // @ts-expect-error No way to override extraOptions type
  const isExternal = Boolean(extraOptions?.external);
  const isMock = isMockModeRequest();
  const result: FetchQueryResult = await fetchQuery(
    {
      ...fetchArgs,
      url: isExternal ? fetchArgs.url : joinBffUrl(bffBaseUrl(), fetchArgs.url),
    },
    api,
    extraOptions
  );
  const noResponse =
    !isExternal &&
    result.error?.status === "FETCH_ERROR" &&
    !api.signal.aborted;
  if (noResponse && isMock) requestMockReactivation();
  // A mock-mode call is answered (or lost) by the worker, never the BFF.
  if (noResponse && !isMock && !IDEMPOTENT_METHODS.has(method))
    noteDirectMutationNetworkError(api, method, fetchArgs.url);
  return result;
};

/**
 * `rawBaseQuery` wrapped with automatic retries + exponential backoff for
 * transient infra failures on idempotent requests (see shouldRetryRequest).
 * A boot-critical fetch (self/balances/products/countries) that hits a momentary
 * upstream outage now recovers silently instead of stranding the user on the
 * splash error page. Retries happen INSIDE this wrapper, so the Sentry capture +
 * OTel log in `baseQuery` below run only on the FINAL result — a blip that
 * recovers produces no event, only a sustained outage reports once.
 *
 * Disabled in test env (mirrors the polling `isTestEnv` convention in
 * `.claude/rules/web/e2e-testing.md`): retry backoff is real wall-clock time
 * (~1.7–5.9s for the 3 retries), which would make error-path E2E tests slow and
 * flaky. Production/UAT keep the full behavior.
 *
 * Telemetry note: because timing in `baseQuery` brackets this wrapper, the
 * per-fetch OTel `duration_ms` of a RETRIED request includes the backoff sleep
 * (not just network time). Only the retried minority is affected; if a Kibana
 * latency dashboard needs to exclude it, add a `retry.attempts` attribute here.
 */
const retryingBaseQuery = isTestEnv
  ? rawBaseQuery
  : retry(rawBaseQuery, {
      retryCondition: (error, args, { attempt }) => {
        // `retry` types the error as `unknown` (it is a generic enhancer); our
        // baseQuery is always a fetchBaseQuery, so it is a FetchBaseQueryError.
        const method =
          typeof args === "string" ? "GET" : (args.method ?? "GET");
        return shouldRetryRequest(
          error as FetchBaseQueryError,
          method,
          attempt
        );
      },
    });

/**
 * On a 401, attempt ONE coalesced silent token renewal so the request can be
 * replayed with a fresh token. This heals the window where the access token
 * lapsed while the UI stayed up (react-oidc-context doesn't flip state on
 * expiry, so nothing else re-runs the request): instead of a failed action /
 * error toast / eventual bounce to Keycloak, the user's click just works.
 *
 * Narrow by design:
 *  - only when a stored user with a refresh token exists (the iframe fallback
 *    without one is unreliable under third-party-cookie blocking, and a
 *    logged-out 401 has nothing to renew);
 *  - the singleton's signinSilent wrapper coalesces a 401 burst from parallel
 *    queries into a single token request, skips renewal in hidden tabs, and
 *    handles transient-failure backoff;
 *  - callers retry the original request at most once — a 401 that survives a
 *    fresh token is a real authorization failure and must surface.
 */
async function renewSessionAfter401(): Promise<boolean> {
  // Mock mode has no real session to renew — a renewal would build the real
  // UserManager and call Keycloak.
  if (isTestEnv || typeof window === "undefined" || isMockModeRequest())
    return false;
  try {
    const userManager = getOidcUserManager();
    if (!userManager) return false;
    const user = await userManager.getUser();
    if (!user?.refresh_token) return false;
    return Boolean(await userManager.signinSilent());
  } catch {
    // Renewal failure is owned by the auth layer (silentRenewError / expiry
    // handling in AuthProvider); here it just means "don't retry".
    return false;
  }
}

/**
 * Wrapped base query that captures HTTP errors to Sentry
 *
 * RTK Query returns errors as { error } objects instead of throwing,
 * so we intercept them here for Sentry reporting.
 */
export const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const startedAt =
    typeof performance !== "undefined" ? performance.now() : Date.now();

  // @ts-expect-error No way to override extraOptions type
  const isExternal = Boolean(extraOptions?.external);
  // Bounded timeout on idempotent GET/HEAD only (never mutations), so a hung
  // boot-critical fetch aborts into TIMEOUT_ERROR → the retry layer → the
  // recoverable boot error page, instead of pending forever below the splash
  // veil. Sizing + exclusions: see withBootFetchTimeout.ts. The same
  // normalized args feed the 401 replay below so both attempts share one
  // policy.
  const requestArgs = withBootFetchTimeout(normalizeFetchArgs(args), {
    isExternal,
    isTestEnv,
  });
  let result = await retryingBaseQuery(requestArgs, api, extraOptions ?? {});
  // Renew-and-retry once on 401 (both idempotent GETs and mutations: a 401
  // means the request was REJECTED before doing any work upstream, so a
  // replay cannot double-apply — unlike the 5xx retry policy above, which
  // must exclude mutations). prepareHeaders re-reads localStorage, so the
  // replay automatically carries the renewed token. Telemetry note: the
  // OTel log + Sentry capture below see only the FINAL result, so a healed
  // 401 produces an INFO log, not an error event.
  if (
    !isExternal &&
    result.error?.status === 401 &&
    (await renewSessionAfter401())
  ) {
    result = await retryingBaseQuery(requestArgs, api, extraOptions ?? {});
  }

  const durationMs = Math.round(
    (typeof performance !== "undefined" ? performance.now() : Date.now()) -
      startedAt
  );

  const url = requestArgs.url;
  const method = (requestArgs.method ?? "GET").toUpperCase();
  const status = result.meta?.response?.status;
  const hadError = !!result.error;

  // One log line per fetch — auto-correlated to the active fetch span by the
  // OTel API (trace_id + span_id stamped on the log record at emit time).
  // INFO for 2xx, ERROR for non-2xx / network failures. Drops silently on
  // any internal error so telemetry never breaks the user's session.
  try {
    apiLogger.emit({
      severityNumber: hadError ? 17 : 9,
      severityText: hadError ? "ERROR" : "INFO",
      body: `${method} ${url} → ${status ?? "—"} (${durationMs}ms)`,
      attributes: {
        "http.method": method,
        "http.url": url,
        ...(status !== undefined ? { "http.status_code": status } : {}),
        duration_ms: durationMs,
        "log.source": "rtk-query",
        "rtk_query.endpoint": api.endpoint,
      },
    });
  } catch {
    /* swallow — never break the user session for a log */
  }

  // Reachability of the BFF origin, judged on the FINAL result (after the
  // retry layer), so one GET retried through a dropped connection counts
  // once. Only calls actually sent to the BFF take part: external calls never
  // match the pattern, and a mock-mode call is answered by the MSW worker.
  const sentTo = result.meta?.request?.url;
  const directPattern =
    isExternal || !sentTo || isMockModeRequest() ? null : bffOriginPattern();
  if (directPattern?.test(sentTo ?? "")) {
    if (result.meta?.response) noteDirectBffReached();
    else if (result.error?.status === "FETCH_ERROR" && !api.signal.aborted)
      noteDirectBffUnreachable({ method, url, endpoint: api.endpoint });
  }

  if (result.error) {
    captureRtkQueryError(result.error, {
      url,
      method,
      endpointName: api.endpoint,
      requestBody: requestArgs.body,
    });
  }

  return result;
};
